import { formatAmount, USD } from "@loot-os/api-contract";
import { Button } from "@loot-os/ui/components/button";
import { Checkbox } from "@loot-os/ui/components/checkbox";
import { DataTable } from "@loot-os/ui/components/data-table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@loot-os/ui/components/dialog";
import { Input } from "@loot-os/ui/components/input";
import { Label } from "@loot-os/ui/components/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@loot-os/ui/components/select";
import { toast } from "@loot-os/ui/components/sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@loot-os/ui/components/tabs";
import { createDataTableColumns } from "@loot-os/ui/lib/dataTable";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Filter } from "lucide-react";
import { createContext, useContext, useState } from "react";

import { ErrorAlert } from "../../components/ErrorAlert";
import {
  DEFAULT_INVENTORY_BOUNDS,
  INVENTORY_CATEGORIES,
  INVENTORY_DOWNLOAD_LIMIT,
  INVENTORY_KIND_LABELS,
  INVENTORY_KINDS,
  INVENTORY_PAGE_SIZE,
  type InventoryCsvRow,
  type InventoryKind,
  inventoryCsv,
  inventoryPriceBounds,
  inventoryQuantityMin,
  printingBadgeClass,
} from "../../lib/inventoryTake";
import { useTRPC } from "../../lib/trpc";
import { RocaBatch } from "./RocaBatch";

const ALL = "all";
const INVENTORY_TABLE_QUERY = ["fulfillment", "inventory-table"] as const;

interface InventoryTableRow {
  id: string;
  name: string;
  imageUrl: string | null;
  value: number | null;
  quantity: number;
  printing: string | null;
}

interface InventorySave {
  savingId: string | null;
  save: (id: string, quantity: number) => void;
}

const InventorySaveContext = createContext<InventorySave | null>(null);

const inventoryColumns = createDataTableColumns<InventoryTableRow>();
const INVENTORY_COLUMNS = inventoryColumns.columns([
  inventoryColumns.accessor("name", {
    header: "Name",
    size: 360,
    cell: ({ row }) => (
      <span className="flex items-center gap-2">
        {row.original.imageUrl ? (
          <img src={row.original.imageUrl} alt="" className="h-10 w-10 object-contain" />
        ) : null}
        <Link
          to="/fulfillment/items/$itemId"
          params={{ itemId: row.original.id }}
          className="underline-offset-4 hover:underline"
        >
          {row.original.name}
        </Link>
      </span>
    ),
    meta: { filter: "text" },
  }),
  inventoryColumns.accessor((row) => row.printing ?? "", {
    id: "printing",
    header: "Printing",
    size: 160,
    cell: ({ row }) =>
      row.original.printing ? (
        <span
          className={`rounded border px-1.5 py-0.5 text-xs ${printingBadgeClass(row.original.printing)}`}
        >
          {row.original.printing}
        </span>
      ) : (
        "—"
      ),
    meta: { filter: "text" },
  }),
  inventoryColumns.accessor("value", {
    header: "Value",
    size: 110,
    cell: ({ getValue }) => {
      const value = getValue();
      return value === null ? "—" : formatAmount(value, USD);
    },
    meta: { align: "end", filter: "number", filterCents: true },
  }),
  inventoryColumns.accessor("quantity", {
    header: "Quantity",
    size: 140,
    cell: ({ row }) => <QuantityCell row={row.original} />,
    meta: { align: "end", filter: "number" },
  }),
  inventoryColumns.accessor("id", {
    header: "Id",
    size: 280,
    cell: ({ getValue }) => <CopyId id={getValue()} />,
    meta: { filter: "text" },
  }),
]);

interface CountFilters {
  kind: InventoryKind | typeof ALL;
  tag: string;
  query: string;
  minPrice: string;
  maxPrice: string;
  quantityAbove: string;
  showArchived: boolean;
}

const OPENING_FILTERS: CountFilters = {
  kind: "SINGLE",
  tag: "POKEMON",
  query: "",
  minPrice: "0",
  maxPrice: "99999",
  quantityAbove: "-999",
  showArchived: false,
};

/** Count on-hand stock for one kind and category, a page of cards at a time. */
export function InventoryPage() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<CountFilters>(OPENING_FILTERS);
  const [search, setSearch] = useState("");
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [earlier, setEarlier] = useState<(string | undefined)[]>([]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [draft, setDraft] = useState(OPENING_FILTERS);
  const [downloading, setDownloading] = useState(false);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [panel, setPanel] = useState("inventory");

  const bounds = inventoryPriceBounds(filters.minPrice, filters.maxPrice);
  const quantityMin = inventoryQuantityMin(filters.quantityAbove);
  const ready = typeof bounds !== "string" && quantityMin !== "invalid";
  const input = {
    query: filters.query.trim() || undefined,
    kind: filters.kind === ALL ? undefined : filters.kind,
    tag: filters.tag === ALL ? undefined : filters.tag,
    valueMin: ready ? bounds.valueMin : DEFAULT_INVENTORY_BOUNDS.valueMin,
    valueMax: ready ? bounds.valueMax : DEFAULT_INVENTORY_BOUNDS.valueMax,
    quantityMin: quantityMin === "invalid" || quantityMin === null ? undefined : quantityMin,
    showArchived: filters.showArchived,
    limit: INVENTORY_PAGE_SIZE,
    cursor,
  };
  const queryOptions = trpc.platform.fulfillment.items.inventory.queryOptions(input);
  const page = useQuery({ ...queryOptions, enabled: ready && panel === "inventory" });
  const tableFilters = {
    query: input.query,
    kind: input.kind,
    tag: input.tag,
    valueMin: input.valueMin,
    valueMax: input.valueMax,
    quantityMin: input.quantityMin,
    showArchived: input.showArchived,
  };
  const table = useQuery({
    queryKey: [...INVENTORY_TABLE_QUERY, tableFilters],
    enabled: ready && panel === "table",
    queryFn: async () => {
      const items: InventoryTableRow[] = [];
      let cursor: string | undefined;
      let truncated = false;
      while (items.length < INVENTORY_DOWNLOAD_LIMIT) {
        const chunk = await queryClient.fetchQuery(
          trpc.platform.fulfillment.items.inventory.queryOptions({
            ...tableFilters,
            limit: 200,
            cursor,
          }),
        );
        items.push(...chunk.items);
        if (!chunk.nextCursor || items.length >= INVENTORY_DOWNLOAD_LIMIT) {
          truncated = Boolean(chunk.nextCursor) && items.length >= INVENTORY_DOWNLOAD_LIMIT;
          break;
        }
        cursor = chunk.nextCursor;
      }
      return { items: items.slice(0, INVENTORY_DOWNLOAD_LIMIT), truncated };
    },
  });

  const saveQuantity = useMutation(
    trpc.platform.fulfillment.items.update.mutationOptions({
      onSuccess: (updated) => {
        toast.success("Quantity saved");
        const withQuantity = <T extends { id: string; quantity: number }>(items: T[]) =>
          items.map((item) =>
            item.id === updated.id ? { ...item, quantity: updated.quantity } : item,
          );
        queryClient.setQueryData(queryOptions.queryKey, (current) => {
          if (!current) return current;
          return { ...current, items: withQuantity(current.items) };
        });
        queryClient.setQueriesData<{ items: InventoryTableRow[]; truncated: boolean }>(
          { queryKey: [...INVENTORY_TABLE_QUERY] },
          (current) => (current ? { ...current, items: withQuantity(current.items) } : current),
        );
      },
      onSettled: () => setSavingId(null),
    }),
  );

  function resetPage() {
    setCursor(undefined);
    setEarlier([]);
  }

  function applySearch() {
    const next = search.trim();
    if (next === filters.query) return;
    setFilters((current) => ({ ...current, query: next }));
    resetPage();
  }

  function closeFilters(open: boolean) {
    if (open) {
      setDraft(filters);
      setFiltersOpen(true);
      return;
    }
    const nextBounds = inventoryPriceBounds(draft.minPrice, draft.maxPrice);
    const nextQuantity = inventoryQuantityMin(draft.quantityAbove);
    if (typeof nextBounds === "string") {
      toast.error(nextBounds);
      return;
    }
    if (nextQuantity === "invalid") {
      toast.error("Quantity above has to be a whole number.");
      return;
    }
    setFilters((current) => ({
      ...current,
      minPrice: draft.minPrice,
      maxPrice: draft.maxPrice,
      quantityAbove: draft.quantityAbove,
      showArchived: draft.showArchived,
    }));
    resetPage();
    setFiltersOpen(false);
  }

  async function download() {
    if (!ready) return;
    setDownloading(true);
    try {
      const rows: InventoryCsvRow[] = [];
      let pageCursor: string | undefined;
      let truncated = false;
      while (rows.length < INVENTORY_DOWNLOAD_LIMIT) {
        const chunk = await queryClient.fetchQuery(
          trpc.platform.fulfillment.items.inventory.queryOptions({
            ...input,
            cursor: pageCursor,
            limit: 200,
          }),
        );
        rows.push(...chunk.items);
        if (!chunk.nextCursor || rows.length >= INVENTORY_DOWNLOAD_LIMIT) {
          truncated = rows.length >= INVENTORY_DOWNLOAD_LIMIT && Boolean(chunk.nextCursor);
          break;
        }
        pageCursor = chunk.nextCursor;
      }
      const file = new Blob([inventoryCsv(rows.slice(0, INVENTORY_DOWNLOAD_LIMIT))], {
        type: "text/csv",
      });
      const url = URL.createObjectURL(file);
      const link = document.createElement("a");
      link.href = url;
      link.download = "assets.csv";
      link.click();
      URL.revokeObjectURL(url);
      if (truncated)
        toast.success("Download stopped at 5,000 items. Narrow the filters for the rest.");
    } catch {
      toast.error("The inventory could not be downloaded.");
    } finally {
      setDownloading(false);
    }
  }

  const items = page.data?.items ?? [];

  return (
    <InventorySaveContext.Provider
      value={{
        savingId,
        save: (id, quantity) => {
          setSavingId(id);
          saveQuantity.mutate({ id, patch: { quantity } });
        },
      }}
    >
      <div className="flex flex-col gap-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Inventory</h1>
            {panel !== "roca" ? (
              <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
                Count what is on hand for one kind and category. A single includes a card whose kind
                was never set. Unclassified custom is a boxed custom item that still has no kind, so
                an older slab is there until someone sets one.
              </p>
            ) : null}
          </div>
          {panel !== "roca" ? (
            <Button
              type="button"
              variant="outline"
              disabled={!ready || downloading}
              onClick={() => void download()}
            >
              {downloading ? "Downloading…" : "Download inventory"}
            </Button>
          ) : null}
        </div>

        <Tabs value={panel} onValueChange={setPanel}>
          <TabsList>
            <TabsTrigger value="inventory">Inventory</TabsTrigger>
            <TabsTrigger value="table">Table</TabsTrigger>
            <TabsTrigger value="roca">Roca</TabsTrigger>
          </TabsList>
          {panel !== "roca" ? (
            <>
              <div className="flex flex-wrap items-end gap-3">
                <FilterSelect
                  label="Kind"
                  value={filters.kind}
                  onChange={(kind) => {
                    setFilters((current) => ({ ...current, kind: kind as CountFilters["kind"] }));
                    resetPage();
                  }}
                  options={[
                    [ALL, "All kinds"],
                    ...INVENTORY_KINDS.map((kind) => [kind, INVENTORY_KIND_LABELS[kind]] as const),
                  ]}
                />
                <FilterSelect
                  label="Category"
                  value={filters.tag}
                  onChange={(tag) => {
                    setFilters((current) => ({ ...current, tag }));
                    resetPage();
                  }}
                  options={[[ALL, "All"], ...INVENTORY_CATEGORIES]}
                />
                <div className="flex min-w-48 flex-1 flex-col gap-1.5">
                  <Label htmlFor="inventory-search">Search</Label>
                  <Input
                    id="inventory-search"
                    value={search}
                    placeholder="Name"
                    onChange={(event) => setSearch(event.target.value)}
                    onBlur={applySearch}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") event.currentTarget.blur();
                    }}
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  aria-label="Filters"
                  onClick={() => closeFilters(true)}
                >
                  <Filter />
                  Filters
                </Button>
              </div>

              <Dialog open={filtersOpen} onOpenChange={closeFilters}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Filters</DialogTitle>
                    <DialogDescription>
                      Price is the item value. Quantity above keeps items with at least that many on
                      hand. Archived items stay out until you include them. Risky grades stay in
                      either way.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <NumberField
                      id="inventory-min-price"
                      label="Min price"
                      value={draft.minPrice}
                      onChange={(minPrice) => setDraft((current) => ({ ...current, minPrice }))}
                    />
                    <NumberField
                      id="inventory-max-price"
                      label="Max price"
                      value={draft.maxPrice}
                      onChange={(maxPrice) => setDraft((current) => ({ ...current, maxPrice }))}
                    />
                    <NumberField
                      id="inventory-quantity-above"
                      label="Quantity above"
                      value={draft.quantityAbove}
                      onChange={(quantityAbove) =>
                        setDraft((current) => ({ ...current, quantityAbove }))
                      }
                    />
                    <div className="flex items-center gap-2 text-sm sm:mt-6">
                      <Checkbox
                        id="inventory-show-archived"
                        checked={draft.showArchived}
                        onCheckedChange={(on) =>
                          setDraft((current) => ({ ...current, showArchived: on }))
                        }
                      />
                      <Label htmlFor="inventory-show-archived">Show archived</Label>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </>
          ) : null}

          <TabsContent value="inventory" className="flex flex-col gap-6">
            {page.isError ? <ErrorAlert message="The inventory could not be loaded." /> : null}

            {page.isLoading ? <p className="text-sm text-muted-foreground">Loading…</p> : null}

            {!page.isLoading && !page.isError && items.length === 0 ? (
              <p className="text-sm text-muted-foreground">No results found.</p>
            ) : null}

            <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
              {items.map((item) => (
                <li
                  key={item.id}
                  className="group relative flex flex-col rounded-md border hover:z-20"
                >
                  <Link
                    to="/fulfillment/items/$itemId"
                    params={{ itemId: item.id }}
                    className="flex flex-1 flex-col rounded-md hover:bg-muted/40"
                  >
                    <p className="line-clamp-2 px-3 py-2 text-center text-sm font-medium">
                      {item.name}
                    </p>
                    <div className="flex flex-1 flex-col gap-2 px-3 pb-2">
                      <div className="relative z-0 flex h-36 items-center justify-center overflow-hidden bg-muted/40 hover:z-10 hover:overflow-visible">
                        {item.imageUrl ? (
                          <img
                            src={item.imageUrl}
                            alt=""
                            className="h-full w-full object-contain transition-transform duration-150 hover:scale-[2.4] hover:drop-shadow-lg"
                          />
                        ) : (
                          <span className="text-xs text-muted-foreground">No image</span>
                        )}
                      </div>
                      <div className="flex items-center justify-between gap-2 text-sm">
                        {item.printing ? (
                          <span
                            className={`truncate rounded border px-1.5 py-0.5 text-xs ${printingBadgeClass(item.printing)}`}
                          >
                            {item.printing}
                          </span>
                        ) : (
                          <span />
                        )}
                        <span>{item.value === null ? "—" : formatAmount(item.value, USD)}</span>
                      </div>
                    </div>
                  </Link>
                  <button
                    type="button"
                    className="truncate px-3 pb-2 text-center font-mono text-[10px] text-muted-foreground hover:text-foreground"
                    aria-label={`Copy item id ${item.id}`}
                    onClick={() => {
                      void navigator.clipboard.writeText(item.id).then(
                        () => toast.success("Copied the item id"),
                        () => toast.error("The item id could not be copied"),
                      );
                    }}
                  >
                    {item.id}
                  </button>
                  <div className="px-3 pb-3">
                    <QuantityField
                      key={`${item.id}:${item.quantity}`}
                      itemId={item.id}
                      quantity={item.quantity}
                      saving={savingId === item.id}
                      onSave={(quantity) => {
                        setSavingId(item.id);
                        saveQuantity.mutate({ id: item.id, patch: { quantity } });
                      }}
                    />
                  </div>
                </li>
              ))}
            </ul>

            {items.length > 0 ? (
              <div className="flex items-center justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  disabled={earlier.length === 0}
                  onClick={() => {
                    const previous = earlier[earlier.length - 1];
                    setEarlier((stack) => stack.slice(0, -1));
                    setCursor(previous);
                  }}
                >
                  Previous
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={!page.data?.nextCursor}
                  onClick={() => {
                    if (!page.data?.nextCursor) return;
                    setEarlier((stack) => [...stack, cursor]);
                    setCursor(page.data.nextCursor);
                  }}
                >
                  Next
                </Button>
              </div>
            ) : null}
          </TabsContent>
          <TabsContent value="table" className="flex flex-col gap-3">
            {table.isError ? <ErrorAlert message="The inventory could not be loaded." /> : null}
            {table.isLoading ? <p className="text-sm text-muted-foreground">Loading…</p> : null}
            {table.data?.truncated ? (
              <p className="text-sm text-muted-foreground">
                Showing the first 5,000. Narrow the filters for the rest.
              </p>
            ) : null}
            {table.data ? (
              <DataTable
                columns={INVENTORY_COLUMNS}
                data={table.data.items}
                getRowId={(row) => row.id}
                search="Filter these rows"
                maxHeight="70vh"
                emptyMessage="No results found."
              />
            ) : null}
          </TabsContent>
          <TabsContent value="roca">
            <RocaBatch />
          </TabsContent>
        </Tabs>
      </div>
    </InventorySaveContext.Provider>
  );
}

function FilterSelect({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: readonly (readonly [string, string])[];
  onChange: (value: string) => void;
}) {
  const id = `inventory-${label.toLowerCase()}`;
  return (
    <div className="flex w-44 flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Select value={value} onValueChange={(next) => next && onChange(next)}>
        <SelectTrigger id={id} className="w-full">
          <SelectValue>
            {(selected) => options.find(([option]) => option === selected)?.[1] ?? selected}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {options.map(([option, optionLabel]) => (
            <SelectItem key={option} value={option}>
              {optionLabel}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function NumberField({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        inputMode="decimal"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
}

function QuantityCell({ row }: { row: InventoryTableRow }) {
  const edit = useContext(InventorySaveContext);
  return (
    <QuantityField
      key={`${row.id}:${row.quantity}`}
      compact
      itemId={row.id}
      itemName={row.name}
      quantity={row.quantity}
      saving={edit?.savingId === row.id}
      onSave={(quantity) => edit?.save(row.id, quantity)}
    />
  );
}

function CopyId({ id }: { id: string }) {
  return (
    <button
      type="button"
      className="truncate font-mono text-xs text-muted-foreground hover:text-foreground"
      aria-label={`Copy item id ${id}`}
      onClick={() => {
        void navigator.clipboard.writeText(id).then(
          () => toast.success("Copied the item id"),
          () => toast.error("The item id could not be copied"),
        );
      }}
    >
      {id}
    </button>
  );
}

function QuantityField({
  itemId,
  itemName,
  quantity,
  saving,
  onSave,
  compact = false,
}: {
  itemId: string;
  itemName?: string;
  quantity: number;
  saving: boolean;
  onSave: (quantity: number) => void;
  compact?: boolean;
}) {
  const [value, setValue] = useState(String(quantity));
  const id = `inventory-qty-${itemId}`;

  function commit() {
    if (!/^\d+$/.test(value.trim())) {
      toast.error("Quantity has to be a whole number, 0 or more.");
      setValue(String(quantity));
      return;
    }
    const next = Number(value);
    if (next === quantity) return;
    onSave(next);
  }

  return (
    <div className="flex items-center gap-2 text-sm">
      {compact ? null : (
        <Label htmlFor={id} className="shrink-0">
          Quantity
        </Label>
      )}
      <Input
        id={id}
        type="number"
        min={0}
        step={1}
        aria-label={compact ? `Quantity for ${itemName ?? "item"}` : undefined}
        className={compact ? "w-24 text-right" : "text-right"}
        value={value}
        disabled={saving}
        onChange={(event) => setValue(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === "Enter") event.currentTarget.blur();
        }}
      />
    </div>
  );
}
